import { makeStyles } from '@mui/styles';
import { Modal } from 'apollo-react';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Layout from './Layout';
import NavigateLinkCell from './NavigateLinkCell';
import QueueMultiSelectCell from './QueueMultiSelectCell';
import { QueueTableActionComponent } from './QueueTableActionComponent';
import { queueProcessRecord } from '../../actions/QueueSetupPageActions';
import SavedSearchesTable from '../../components/SavedSearchesTable';
import { selectAllHeader } from '../../components/TableSelect/MultiSelectHeader';
import { ACTION_NAME } from '../../constants/QueueSetupConstant';
import { ENTITY_DISPLAY, WORKFLOW_QUEUE_SETUP_PAGE } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getActionName } from '../../selectors/QueueSetupPageSelectors';
import { getPagePathname } from '../../utils/childrenWindowsUtils';
import { removeCurrentPageSelection } from '../../utils/commonUtils';
import WorkflowNavigateLinkCell from '../FindPage/NavigateLinkCell';

const useStyles = makeStyles({
  btnRightAlign: {
    float: 'right',
  },
});

const getIds = (rows) => {
  const values = rows.map((row) => {
    const keys = Object.keys(row);
    for (let i = 0; i < keys.length; i++) {
      if (keys.includes('idx')) {
        return row[keys[i + 1]];
      }
      return row[keys[i]];
    }
  });
  return values;
};

const MonitoringQueueSetupPage = ({ location }) => {
  const classes = useStyles();
  const [selectedRowItems, setSelectedRowItems] = useState([]);
  const [isPopup, setIsPopup] = useState(false);
  const [actionName, setActionName] = useState('');
  const dispatch = useDispatch();
  const { entityName, searchName } = qs.parse(location.search);
  const pageTitle = searchName;
  const translate = useTranslation();

  const menuAction = useSelector(getActionName(entityName));

  const route = getPagePathname();
  const isWorkflow = route === WORKFLOW_QUEUE_SETUP_PAGE;

  const handleClick = (actionName) => {
    if (actionName && actionName.toLowerCase() === ACTION_NAME.DELETE) {
      setActionName(actionName);
      setIsPopup(true);
    } else {
      dispatch(queueProcessRecord(entityName, actionName, getIds(selectedRowItems), translate));
      setSelectedRowItems([]);
    }
  };

  const onDeleteRecord = () => {
    setIsPopup(false);
    dispatch(queueProcessRecord(entityName, actionName, getIds(selectedRowItems), translate));
    setSelectedRowItems([]);
  };

  useEffect(() => {
    document.title = pageTitle || 'Monitoring Queue';
    setSelectedRowItems([]);
  }, [pageTitle]);

  if (_.isNil(entityName)) {
    return <Layout>{translate('Please provide an entity name.')}</Layout>;
  }

  const shouldBeDisabled = (row) => row.count === 0;

  const handleMultiSelect = (checked, row) => {
    const newSelections = checked
      ? [...selectedRowItems, row]
      : selectedRowItems.filter((s) => s[s.PrimaryKeyColumn] !== row[row.PrimaryKeyColumn]);

    setSelectedRowItems(newSelections);
  };

  const handleHeaderMultiselect = (
    allRowsPerPage,
    isAllRecordsPerPageSelected,
    isSelectedRecordOnCurrentPage
  ) => {
    if (
      (isSelectedRecordOnCurrentPage && !isAllRecordsPerPageSelected) ||
      isAllRecordsPerPageSelected
    ) {
      const filteredSelections = removeCurrentPageSelection(allRowsPerPage, selectedRowItems);

      setSelectedRowItems(filteredSelections);

      return;
    }

    const rowsAllowedToBeSelected = allRowsPerPage.filter((row) => !shouldBeDisabled(row));

    setSelectedRowItems([...selectedRowItems, ...rowsAllowedToBeSelected]);
  };

  const extraColumns = [
    {
      index: 0,
      header: (rows) =>
        selectAllHeader(rows, handleHeaderMultiselect, selectedRowItems, shouldBeDisabled),
      accessor: 'SELECT_ALL_HEADER',
      fixedWidth: true,
      customCell: QueueMultiSelectCell(selectedRowItems, handleMultiSelect),
    },
  ];

  return (
    <Layout pageTitle={pageTitle}>
      <div className={classes.btnRightAlign}>
        <QueueTableActionComponent
          selectedRows={selectedRowItems}
          menuAction={menuAction}
          handleClick={handleClick}
        />
      </div>

      <SavedSearchesTable
        customCell={
          isWorkflow ? WorkflowNavigateLinkCell.forUrl(ENTITY_DISPLAY) : NavigateLinkCell.fromColumn
        }
        entityName={entityName}
        initialSearchName={searchName}
        enableSorting
        rowsPerPageOptions={[10, 25, 50]}
        title={translate('Records')}
        extraColumns={menuAction !== '' ? extraColumns : []}
        showSearchSelector={false}
      />
      <Modal
        open={isPopup}
        variant="warning"
        onClose={() => setIsPopup(false)}
        title={translate('Record Delete Confirmation')}
        message={translate('Are you sure to delete the record')}
        buttonProps={[{}, { label: translate('Ok'), onClick: onDeleteRecord }]}
      />
    </Layout>
  );
};
export default MonitoringQueueSetupPage;
